// Pure helpers for the sales analytics views. No date library: native Date + Intl.
//
// Time zone: the API buckets in the app zone, which is UTC (see the API contract),
// so every calendar-day computation here uses the UTC getters/setters. Using local
// getters would shift "today" by a day for admins east/west of UTC near midnight.

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 24 * 60 * 60 * 1000;

export const PRESETS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'ytd', label: 'This year' },
  { key: 'all', label: 'All time' },
];

export const GRANULARITIES = [
  { key: 'auto', label: 'Auto' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

export const DEFAULT_PRESET = '30d';

export const toISODate = (date) => date.toISOString().slice(0, 10);

// Returns a Date at UTC midnight, or null when the string is not a real calendar day
// (Date.UTC silently rolls 2026-02-30 into March, so the round-trip check catches it).
export const parseISODate = (value) => {
  const m = ISO_DATE.exec(value || '');
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return toISODate(date) === value ? date : null;
};

const utcToday = (now) => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
const minusDays = (date, days) => new Date(date.getTime() - days * DAY_MS);

// Preset -> inclusive {from, to}. "All time" is bounded by the API's 10 year clamp,
// so we ask for exactly 10 years instead of triggering meta.clamped.
export const presetRange = (preset, now = new Date()) => {
  const today = utcToday(now);
  const to = toISODate(today);
  switch (preset) {
    case '7d': return { from: toISODate(minusDays(today, 6)), to };
    case '90d': return { from: toISODate(minusDays(today, 89)), to };
    case 'ytd': return { from: `${today.getUTCFullYear()}-01-01`, to };
    case 'all': {
      const from = new Date(today.getTime());
      from.setUTCFullYear(from.getUTCFullYear() - 10);
      return { from: toISODate(from), to };
    }
    case '30d':
    default:
      return { from: toISODate(minusDays(today, 29)), to };
  }
};

// Range state ({preset, from, to, granularity, currency}) -> API query params.
// Granularity "auto" and an unset currency are omitted so the server picks.
export const rangeParams = (range, now = new Date()) => {
  const { from, to } = range.preset === 'custom' && range.from && range.to
    ? { from: range.from, to: range.to }
    : presetRange(range.preset, now);
  const params = { from, to };
  if (range.granularity && range.granularity !== 'auto') params.granularity = range.granularity;
  if (range.currency) params.currency = range.currency;
  return params;
};

// Mirrors the API bound on explicit granularity (span = to - from in days): longer
// spans would return thousands of buckets, so the API answers 400 and the UI disables
// the option. Month and auto are always allowed (the API clamps ranges to 10 years).
export const GRANULARITY_MAX_SPAN_DAYS = { day: 366, week: 1830 };

export const spanDays = (from, to) => {
  const f = parseISODate(from);
  const t = parseISODate(to);
  return f && t ? Math.round((t - f) / DAY_MS) : 0;
};

export const granularityAllowed = (granularity, from, to) => {
  const max = GRANULARITY_MAX_SPAN_DAYS[granularity];
  return max === undefined || spanDays(from, to) <= max;
};

export const validateRange = (from, to) => {
  const f = parseISODate(from);
  const t = parseISODate(to);
  if (!f) return 'Enter a valid start date.';
  if (!t) return 'Enter a valid end date.';
  if (t < f) return 'End date must be on or after the start date.';
  return null;
};

const currencyFormatters = {};
export const formatCurrency = (cents, currency = 'usd') => {
  const code = (currency || 'usd').toUpperCase();
  if (!currencyFormatters[code]) {
    currencyFormatters[code] = new Intl.NumberFormat('en-US', { style: 'currency', currency: code });
  }
  return currencyFormatters[code].format((cents || 0) / 100);
};

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1,
});
// The API sends percentages as numbers like 25.0 (already x100, one decimal).
export const formatPercent = (pct) => percentFormatter.format((Number(pct) || 0) / 100);

export const formatCount = (n) => (Number(n) || 0).toLocaleString('en-US');

const dayFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const shortFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

export const formatBucketLabel = (iso, granularity) => {
  const date = parseISODate(iso);
  if (!date) return iso;
  if (granularity === 'month') return monthFmt.format(date);
  if (granularity === 'week') return `Week of ${dayFmt.format(date)}`;
  return dayFmt.format(date);
};

// Compact axis tick label (the tooltip carries the full label).
export const formatAxisLabel = (iso, granularity) => {
  const date = parseISODate(iso);
  if (!date) return iso;
  return granularity === 'month' ? monthFmt.format(date) : shortFmt.format(date);
};

export const formatRangeLabel = (from, to) => {
  const f = parseISODate(from);
  const t = parseISODate(to);
  if (!f || !t) return '';
  return from === to ? dayFmt.format(f) : `${dayFmt.format(f)} to ${dayFmt.format(t)}`;
};

// RFC 4180 style: quote any cell containing a comma, quote or newline; double inner quotes.
// Text that a spreadsheet would run as a formula (leading = + - @ tab CR) gets a leading
// apostrophe (OWASP CSV injection guidance); plain numbers such as -12.50 are left alone.
const FORMULA_START = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;
const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  let s = String(value);
  if (typeof value === 'string' && FORMULA_START.test(s) && !PLAIN_NUMBER.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export const buildCsv = (rows) => rows.map((row) => row.map(csvCell).join(',')).join('\r\n');

// Net figures for a bucket / range / row. Responses from before refund tracking have
// no net keys, so fall back to the gross ones (nothing refunded).
export const netCents = (r) => (r?.net_cents ?? r?.gross_cents ?? 0);
export const netSalesCount = (r) => (r?.net_sales_count ?? r?.sales_count ?? 0);

const decimal = (cents) => ((cents || 0) / 100).toFixed(2);

// Amounts are plain decimals (no symbol, no grouping) so spreadsheets parse them as numbers.
export const bucketCsv = (series, { currency = 'usd', granularity = 'day' } = {}) => {
  const code = currency.toUpperCase();
  return buildCsv([
    [
      'Period', 'Sales', 'Net sales', 'Refunded sales',
      `Gross revenue (${code})`, `Refunded (${code})`, `Net revenue (${code})`,
      '% of video range net revenue', '% of all net sales revenue in period',
    ],
    ...(series || []).map((b) => [
      formatBucketLabel(b.bucket_start, granularity),
      b.sales_count || 0,
      netSalesCount(b),
      b.refund_count || 0,
      decimal(b.gross_cents),
      decimal(b.refunded_cents),
      decimal(netCents(b)),
      (Number(b.pct_of_range_revenue) || 0).toFixed(1),
      (Number(b.pct_of_bucket_revenue) || 0).toFixed(1),
    ]),
  ]);
};

// Client-side download via Blob + object URL (no dependency). No-op where unsupported (jsdom).
export const downloadCsv = (filename, csv) => {
  if (typeof window === 'undefined' || !window.URL || !window.URL.createObjectURL) return;
  const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const isAbortError = (err) =>
  !!err && (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED');

export const prefersReducedMotion = () =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    ? true
    : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
